import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency in Indian Rupees
export function formatCurrency(value, options = {}) {
  const { compact = false, decimals = 0 } = options;
  
  if (value === null || value === undefined) return '₹0';
  
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  if (compact) {
    if (absValue >= 10000000) {
      return `${sign}₹${(absValue / 10000000).toFixed(1)}Cr`;
    } else if (absValue >= 100000) {
      return `${sign}₹${(absValue / 100000).toFixed(1)}L`;
    } else if (absValue >= 1000) {
      return `${sign}₹${(absValue / 1000).toFixed(1)}K`;
    }
  }
  
  return `${sign}₹${absValue.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

// Format percentage
export function formatPercent(value, options = {}) {
  const { decimals = 1, showSign = false } = options;
  
  if (value === null || value === undefined) return '0%';
  
  const formatted = Math.abs(value).toFixed(decimals);
  const sign = showSign && value > 0 ? '+' : (value < 0 ? '-' : '');
  
  return `${sign}${formatted}%`;
}

// Format number with Indian locale
export function formatNumber(value, options = {}) {
  const { decimals = 0 } = options;
  
  if (value === null || value === undefined) return '0';
  
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Get status color class
export function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'good':
    case 'on_track':
      return 'status-good';
    case 'warning':
      return 'status-warning';
    case 'critical':
    case 'error':
      return 'status-critical';
    default:
      return 'bg-slate-100 text-slate-700 border border-slate-200';
  }
}

// Get trend icon and color
export function getTrendInfo(value, isPositiveGood = true) {
  const isPositive = value > 0;
  const isGood = isPositiveGood ? isPositive : !isPositive;
  
  return {
    icon: isPositive ? 'TrendingUp' : 'TrendingDown',
    color: isGood ? 'text-emerald-600' : 'text-red-600',
    bgColor: isGood ? 'bg-emerald-50' : 'bg-red-50',
  };
}

// Calculate variance
export function calculateVariance(actual, planned) {
  if (!planned || planned === 0) return { amount: 0, percent: 0 };
  
  const amount = actual - planned;
  const percent = (amount / planned) * 100;
  
  return {
    amount,
    percent,
  };
}

// Month names
export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

// Channel display names
export const CHANNEL_NAMES = {
  meta: 'Meta Ads',
  google: 'Google Ads',
  amazon: 'Amazon',
  blinkit: 'Blinkit',
  d2c: 'D2C Website',
  offline: 'Offline Retail',
};

// Chart colors
export const CHART_COLORS = [
  '#DC5829', // Salty Orange
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#E87D59', // Orange light
  '#EC4899', // Pink
];

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Date formatting
export function formatDate(date, format = 'short') {
  const d = new Date(date);
  
  if (format === 'short') {
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } else if (format === 'medium') {
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } else {
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  }
}
